// src/pages/ProjectDetails.js
// Admin-only, in-app "Project Details" page. Renders a project's rich `details`
// content blocks in the report style (KPIs, donut, bars, tables, findings,
// steps, notes). Backed by GET /api/infrastructure-projects/:id (requireRole admin).
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Ticket, Target, ShieldCheck } from "lucide-react";
import useIsMobile from "../hooks/useIsMobile";

const API = "/api";
const tok = () => localStorage.getItem("midtc_token") || "";

const STATUS_COLOR = {
  "Planning": "#4f6fff", "In Progress": "#7b61ff", "On Hold": "#ffb547",
  "Completed": "#3bffb1", "Cancelled": "#8d99ae",
};
const gradeColor = g => ({ A:"#3bffb1", B:"#5fffc4", C:"#ffb547", D:"#ff8a5c", F:"#ff5470" }[g] || "#8d99ae");

// ── Block renderers ──────────────────────────────────────────────
function Block({ b }) {
  const isMobile = useIsMobile();
  if (!b || !b.type) return null;
  switch (b.type) {
    case "kpis":
      return (
        <div style={S.panel}>
          {b.title && <h2 style={S.h2}>{b.title}</h2>}
          <div style={{ display:"grid", gridTemplateColumns:`repeat(${Math.min(b.items.length, isMobile ? 2 : 4)},1fr)`, gap:14 }}>
            {b.items.map((k,i) => (
              <div key={i} style={S.kpi}>
                <div style={{ fontSize:28, fontWeight:800, lineHeight:1, color:k.color || "var(--t-text-primary)" }}>{k.value}</div>
                <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:"0.05em", color:"var(--t-text-muted)", fontWeight:700, marginTop:6 }}>{k.label}</div>
              </div>
            ))}
          </div>
        </div>
      );
    case "note": {
      const c = b.variant === "danger" ? "#ff5470" : b.variant === "warn" ? "#ffb547" : "#4f6fff";
      return <div style={{ ...S.panel, borderLeft:`3px solid ${c}`, background:`${c}10` }}><div style={{ fontSize:13.5, lineHeight:1.6, color:"var(--t-text-body)" }} dangerouslySetInnerHTML={{ __html: b.html }} /></div>;
    }
    case "donut": {
      const total = b.slices.reduce((a,s)=>a+s.value,0) || 1;
      let acc = 0; const segs = b.slices.map(s => { const st = acc/total*100, en=(acc+s.value)/total*100; acc+=s.value; return `${s.color} ${st}% ${en}%`; });
      return (
        <div style={S.panel}>
          {b.title && <h2 style={S.h2}>{b.title}</h2>}
          <div style={{ display:"flex", gap:24, alignItems:"center", flexWrap:"wrap" }}>
            <div style={{ width:160, height:160, borderRadius:"50%", position:"relative", flexShrink:0, background:`conic-gradient(${segs.join(",")})` }}>
              <div style={{ position:"absolute", inset:32, borderRadius:"50%", background:"var(--t-page)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                {b.center && <b style={{ fontSize:23 }}>{b.center}</b>}
                {b.centerLabel && <span style={{ fontSize:9, color:"var(--t-text-faint)", textTransform:"uppercase", letterSpacing:"0.05em" }}>{b.centerLabel}</span>}
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
              {b.slices.map((s,i) => <div key={i} style={{ display:"flex", alignItems:"center", gap:9, fontSize:13, color:"var(--t-text-muted)" }}><span style={{ width:11, height:11, borderRadius:3, background:s.color }} />{s.label}</div>)}
            </div>
          </div>
        </div>
      );
    }
    case "bars":
      return (
        <div style={S.panel}>
          {b.title && <h2 style={S.h2}>{b.title}</h2>}
          {b.items.map((it,i) => {
            const col = it.color || gradeColor(it.grade);
            return (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:12, margin:"9px 0", fontSize:12.5 }}>
                <span style={{ width: isMobile ? 110 : 185, color:"var(--t-text-muted)" }}>{it.label}</span>
                <span style={{ flex:1, height:10, background:"rgba(255,255,255,0.06)", borderRadius:99, overflow:"hidden" }}>
                  <i style={{ display:"block", height:"100%", width:`${it.value}%`, borderRadius:99, background:`linear-gradient(90deg,${col}aa,${col})` }} />
                </span>
                <span style={{ width:64, textAlign:"right", fontWeight:800, color:col }}>{it.value}{it.grade?` · ${it.grade}`:""}</span>
              </div>
            );
          })}
          {b.foot && <div style={{ fontSize:11.5, color:"var(--t-text-faint)", marginTop:10 }}>{b.foot}</div>}
        </div>
      );
    case "table":
      return (
        <div style={S.panel}>
          {b.title && <h2 style={S.h2}>{b.title}</h2>}
          <div style={{ overflowX: isMobile ? "auto" : "visible" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead><tr>{b.columns.map((c,i)=><th key={i} style={{ textAlign: i===0?"left":"center", padding:"10px 12px", fontSize:10, textTransform:"uppercase", letterSpacing:"0.04em", color:"var(--t-text-faint)", borderBottom:"1px solid var(--t-border-soft)", fontWeight:700 }}>{c}</th>)}</tr></thead>
            <tbody>
              {b.rows.map((row,ri) => (
                <tr key={ri}>{row.map((cell,ci) => (
                  <td key={ci} style={{ padding:"11px 12px", borderBottom:"1px solid var(--t-border-softer)", textAlign: ci===0?"left":"center", color:"var(--t-text-body)" }}>
                    {cell && typeof cell === "object"
                      ? <span style={{ display:"inline-flex", padding:"3px 9px", borderRadius:99, fontSize:10.5, fontWeight:700, background:`${cell.color}1f`, color:cell.color, border:`1px solid ${cell.color}55` }}>{cell.badge}</span>
                      : <span dangerouslySetInnerHTML={{ __html: String(cell) }} />}
                  </td>
                ))}</tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      );
    case "findings":
      return (
        <div style={S.panel}>
          {b.title && <h2 style={S.h2}>{b.title}</h2>}
          <ul style={{ listStyle:"none", display:"flex", flexDirection:"column", gap:11 }}>
            {b.items.map((h,i)=><li key={i} style={{ fontSize:13.5, lineHeight:1.6, color:"var(--t-text-muted)", paddingLeft:22, position:"relative" }}><span style={{ position:"absolute", left:0, color:"#4f6fff" }}>▸</span><span dangerouslySetInnerHTML={{ __html:h }} /></li>)}
          </ul>
        </div>
      );
    case "steps":
      return (
        <div style={S.panel}>
          {b.title && <h2 style={S.h2}>{b.title}</h2>}
          {b.items.map((st,i) => (
            <div key={i} style={{ display:"flex", gap:13, padding:"12px 0", borderTop: i?"1px solid var(--t-border-soft)":"none" }}>
              <div style={{ width:26, height:26, borderRadius:8, flexShrink:0, background:"linear-gradient(135deg,#4f6fff,#7b61ff)", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:12, color:"#fff" }}>{st.n ?? i+1}</div>
              <div style={{ fontSize:13.5, lineHeight:1.55, color:"var(--t-text-muted)" }}><b style={{ color:"var(--t-text-primary)" }}>{st.title}</b>{st.body ? ` — ${st.body}` : ""}</div>
            </div>
          ))}
        </div>
      );
    default: return null;
  }
}

const S = {
  page:  { minHeight:"100vh", background:"radial-gradient(circle at 12% -5%, rgba(79,111,255,0.16), transparent 42%), var(--t-page)", color:"var(--t-text-primary)", padding:"28px 32px", maxWidth:1100, margin:"0 auto" },
  back:  { background:"none", border:"none", color:"var(--t-text-faint)", cursor:"pointer", fontSize:13, marginBottom:14, display:"flex", alignItems:"center", gap:6, fontFamily:"inherit" },
  panel: { background:"linear-gradient(180deg,var(--t-panel),var(--t-surface-deep))", border:"1px solid var(--t-border-soft)", borderRadius:16, padding:22, marginTop:18, boxShadow:"var(--t-shadow-panel)" },
  h2:    { fontSize:15, fontWeight:700, marginBottom:16, color:"var(--t-text-primary)" },
  kpi:   { background:"linear-gradient(180deg,var(--t-panel),var(--t-surface-deep))", border:"1px solid var(--t-border-soft)", borderRadius:14, padding:16 },
};

export default function ProjectDetails() {
  const isMobile = useIsMobile();
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/infrastructure-projects/${id}`, { headers: { Authorization: `Bearer ${tok()}` } });
      if (r.status === 403) { setErr("forbidden"); return; }
      if (!r.ok) { setErr("notfound"); return; }
      setP(await r.json());
    } catch { setErr("error"); } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const page = isMobile ? { ...S.page, padding: "18px 14px" } : S.page;

  if (loading) return <div style={{ ...page, display:"flex", alignItems:"center", justifyContent:"center", height:"60vh" }}><div style={{ width:30, height:30, border:"3px solid #4f6fff", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;
  if (err === "forbidden") return <div style={page}><div style={{ ...S.panel, textAlign:"center", padding:40 }}><ShieldCheck size={28} style={{ color:"#ff5470" }} /><h2 style={{ marginTop:10 }}>Admin access required</h2><p style={{ color:"var(--t-text-muted)", fontSize:13 }}>Project details pages are restricted to administrators.</p></div></div>;
  if (err || !p) return <div style={page}><button style={S.back} onClick={()=>navigate("/infrastructure")}><ArrowLeft size={15}/> Back</button><div style={{ ...S.panel, textAlign:"center", padding:40, color:"var(--t-text-muted)" }}>Project not found.</div></div>;

  const sc = STATUS_COLOR[p.status] || "#8d99ae";
  const details = Array.isArray(p.details) ? p.details : [];
  const tasks = Array.isArray(p.tasks) ? p.tasks : [];
  const taskDone = tasks.filter(t=>t.done).length;
  const pct = tasks.length ? Math.round(taskDone/tasks.length*100) : null;

  return (
    <div style={page}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <button style={S.back} onClick={()=>navigate("/infrastructure")}><ArrowLeft size={15}/> Back to Infrastructure</button>

      {/* Header */}
      <div style={{ display:"flex", alignItems:"flex-start", gap:16, flexWrap:"wrap" }}>
        <div style={{ width:52, height:52, clipPath:"polygon(25% 5%,75% 5%,100% 50%,75% 95%,25% 95%,0 50%)", background:"linear-gradient(135deg,#4f6fff,#7b61ff)", display:"flex", alignItems:"center", justifyContent:"center", filter:"drop-shadow(0 0 10px rgba(79,111,255,.5))", flexShrink:0 }}>
          <ShieldCheck size={24} color="#fff" />
        </div>
        <div style={{ flex:1, minWidth:240 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:5 }}>
            <span style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:11, fontWeight:700, color:sc, textTransform:"uppercase", letterSpacing:"0.05em" }}><span style={{ width:8, height:8, borderRadius:"50%", background:sc, boxShadow:`0 0 8px ${sc}` }} />{p.status}</span>
            <span style={{ fontSize:11, fontWeight:700, color: p.priority==="Critical"||p.priority==="High" ? "#ff8a5c":"var(--t-text-faint)" }}>{p.priority} priority</span>
            {p.client_name && <span style={{ fontSize:12, color:"var(--t-text-faint)" }}>· {p.client_name}</span>}
          </div>
          <h1 style={{ fontSize:24, fontWeight:800, letterSpacing:"-0.5px" }}>{p.title}</h1>
          {p.goal && <div style={{ display:"flex", alignItems:"center", gap:7, marginTop:6, fontSize:13, color:"var(--t-text-muted)" }}><Target size={14} style={{ color:"#7b61ff" }} /> {p.goal}</div>}
          {p.source_ticket_number && (
            <button onClick={()=>navigate(`/tickets/${p.source_ticket_id}`)} style={{ marginTop:10, display:"inline-flex", alignItems:"center", gap:7, padding:"6px 13px", borderRadius:99, background:"rgba(79,111,255,0.1)", border:"1px solid rgba(79,111,255,0.25)", color:"#cdd9ef", fontSize:12.5, fontWeight:600, cursor:"pointer" }}>
              <Ticket size={13}/> Linked ticket #{p.source_ticket_number}
            </button>
          )}
        </div>
        {pct !== null && (
          <div style={{ textAlign:"center", flexShrink:0 }}>
            <div style={{ fontSize:30, fontWeight:800, color: pct===100?"#3bffb1":"#7088ff" }}>{pct}%</div>
            <div style={{ fontSize:10, color:"var(--t-text-faint)", textTransform:"uppercase", letterSpacing:"0.05em", fontWeight:700 }}>{taskDone}/{tasks.length} tasks</div>
          </div>
        )}
      </div>

      {/* Rich detail blocks */}
      {details.length > 0
        ? details.map((b,i) => <Block key={i} b={b} />)
        : <div style={{ ...S.panel, color:"var(--t-text-muted)", fontSize:13.5, lineHeight:1.6 }}>{p.description || "No detailed report content has been added to this project yet."}</div>}

      <div style={{ textAlign:"center", color:"var(--t-text-faint)", fontSize:11, marginTop:26 }}>
        Admin-only project details · MIDTC Managed Services
      </div>
    </div>
  );
}
